import { useMemo, useState } from 'preact/hooks';
import {
  bulletinSubjectFor,
  lessonDomainsFor,
  suggestedBulletinDomain,
} from '../../lib/bulletin-subjects.ts';
import { isValidScore } from '../../lib/bulletin.ts';
import { linkedLessonIds } from '../../lib/lesson-links.ts';
import { addDays, frDayDate, parseLocalDate, todayISO, toLocalISO } from '../../lib/dates.ts';
import { SUBJECTS } from '../../lib/subjects.ts';
import { live, useStore } from '../../store/store.ts';
import { studentDisplayName } from '../../lib/student-roster.ts';
import type { Lesson } from '../../store/types.ts';
import { closeModal, toast } from '../../ui/ui-store.ts';

const PERIODS = ['1', '2', '3'] as const;

/** The calendar date a lesson sits on, from its week and its weekday. */
function lessonDate(lesson: Lesson): string {
  return toLocalISO(addDays(parseLocalDate(lesson.weekStart), lesson.day));
}

/**
 * Encoding an evaluation of one lesson, for the whole class.
 *
 * Replaces the old "évaluation de toute la classe", which asked for a title
 * that had already been typed once as a lesson. Here the lesson is picked from
 * the planner and everything else follows from it.
 */
export function LessonEvaluationModal() {
  // `live()` builds a new array, so it must run outside the selector:
  // useSyncExternalStore treats a fresh reference as a changed snapshot and
  // would re-render forever.
  const students = live(useStore((s) => s.students));
  const lessons = live(useStore((s) => s.lessons));
  const evaluations = live(useStore((s) => s.evaluations));

  const [subject, setSubject] = useState(SUBJECTS[0][0] as string);
  const [domain, setDomain] = useState('');
  const [lessonId, setLessonId] = useState('');
  const [title, setTitle] = useState('');
  const [max, setMax] = useState('20');
  const [period, setPeriod] = useState('1');
  const [date, setDate] = useState('');
  const [scores, setScores] = useState<Record<string, string>>({});

  const block = bulletinSubjectFor(subject);
  const domains = block?.domains ?? [];

  /** Lessons of this subject, newest first, optionally narrowed by domain. */
  const candidates = useMemo(() => {
    const wanted = lessonDomainsFor(domain);
    return lessons
      .filter((l) => l.subject === subject)
      .filter((l) => wanted.length === 0 || wanted.includes(l.domain))
      .map((l) => ({ lesson: l, date: lessonDate(l) }))
      .sort((a, b) => b.date.localeCompare(a.date) || b.lesson.slot - a.lesson.slot);
  }, [lessons, subject, domain]);

  /** Lessons already carrying marks, so the list can say so. */
  const graded = useMemo(() => {
    const ids = new Set<string>();
    // A duplicated lesson carries the marks of its original, so it says so too.
    for (const e of evaluations) for (const id of linkedLessonIds(e)) ids.add(id);
    return ids;
  }, [evaluations]);

  function chooseSubject(next: string) {
    setSubject(next);
    setDomain('');
    setLessonId('');
  }

  function chooseLesson(lesson: Lesson, on: string) {
    setLessonId(lesson.id);
    setTitle(lesson.title);
    setDate(on);
    const suggested = suggestedBulletinDomain(lesson.subject, lesson.domain);
    if (suggested !== '') setDomain(suggested);
  }

  function save() {
    if (!title.trim()) {
      toast('Choisis une leçon ou donne un nom à l’évaluation');
      return;
    }
    if (!students.length) {
      toast('Ajoute d’abord des élèves');
      return;
    }

    const invalid = students.find((s) => !isValidScore(scores[s.id] ?? ''));
    if (invalid) {
      toast(`Points illisibles pour ${studentDisplayName(invalid.name)} — un nombre ou « A »`);
      return;
    }

    const n = useStore.getState().addLessonEvaluations(
      {
        subject,
        domain: domains.length ? domain : '',
        lessonId,
        title: title.trim(),
        max: max.trim() || '20',
        period,
        date: date || todayISO(),
      },
      students.map((s) => ({ studentId: s.id, score: scores[s.id] ?? '' })),
    );

    closeModal();
    toast(`${n} résultat(s) enregistré(s)`);
  }

  return (
    <>
      <p class="muted">
        Choisis la leçon évaluée : son titre devient le nom de l’évaluation, et les points sont
        rangés dans le dossier de chaque élève.
      </p>

      <div class="formGrid">
        <div>
          <label for="lesub">Matière</label>
          <select id="lesub" value={subject} onChange={(e) => chooseSubject(e.currentTarget.value)}>
            {SUBJECTS.map(([name]) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        {domains.length > 0 && (
          <div>
            <label for="ledomain">Domaine du bulletin</label>
            <select
              id="ledomain"
              value={domain}
              onChange={(e) => setDomain(e.currentTarget.value)}
            >
              <option value="">Tous les domaines</option>
              {domains.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div class="detailSection">
        <label>Leçon évaluée</label>
        <div class="lessonPicker">
          {candidates.length === 0 ? (
            <div class="empty">Aucune leçon encodée pour cette matière.</div>
          ) : (
            candidates.map(({ lesson, date: on }) => (
              <button
                key={lesson.id}
                class={lesson.id === lessonId ? 'lessonPick active' : 'lessonPick'}
                onClick={() => chooseLesson(lesson, on)}
              >
                <div>
                  <div>{lesson.title}</div>
                  <div class="small muted">
                    {frDayDate(parseLocalDate(on))}
                    {lesson.domain ? ` • ${lesson.domain}` : ''}
                  </div>
                </div>
                {graded.has(lesson.id) && <span class="tag">✓ évaluée</span>}
              </button>
            ))
          )}
        </div>
      </div>

      <div class="formGrid">
        <div style={{ gridColumn: '1/-1' }}>
          <label for="letitle">Nom de l’évaluation</label>
          <input
            id="letitle"
            value={title}
            placeholder="Ex. Calcul mental"
            onInput={(e) => setTitle(e.currentTarget.value)}
          />
        </div>

        <div>
          <label for="lemax">Sur combien de points ?</label>
          <input
            id="lemax"
            type="number"
            step="0.5"
            value={max}
            onInput={(e) => setMax(e.currentTarget.value)}
          />
        </div>

        <div>
          <label for="leperiod">Période bulletin</label>
          <select id="leperiod" value={period} onChange={(e) => setPeriod(e.currentTarget.value)}>
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label for="ledate">Date</label>
          <input
            id="ledate"
            type="date"
            value={date}
            onInput={(e) => setDate(e.currentTarget.value)}
          />
        </div>
      </div>

      <div class="detailSection">
        <label>Points</label>
        <p class="small muted" style={{ margin: '0 0 8px' }}>
          « A » pour un élève absent. Laissé vide, le résultat vaut 0.
        </p>
        <div class="evalGrid">
          {students.map((s) => (
            <div key={s.id} class="evalStudent">
              <b>{studentDisplayName(s.name)}</b>
              <input
                type="text"
                // Not `decimal`: that hint makes the tablet's handwriting
                // recogniser expect digits, and turns a stylus "a" into a 4.
                inputMode="text"
                autoComplete="off"
                autoCorrect="off"
                spellcheck={false}
                aria-label={`Points de ${studentDisplayName(s.name)}`}
                value={scores[s.id] ?? ''}
                onInput={(e) =>
                  setScores((current) => ({ ...current, [s.id]: e.currentTarget.value }))
                }
              />
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: '15px' }}>
        <button class="btn primary" onClick={save}>
          Enregistrer les résultats
        </button>
      </div>
    </>
  );
}
